import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import {
  buildLoginPath,
  isAnonymousReadablePath,
} from '../common/authRedirect';
import { useAuth } from './AuthContext';
import { hasLoggedInBefore } from './priorLoginCookie';
import { touchNavButtonSx } from '../common/touchTargetSx';

const sessionCheckSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '40vh',
  px: 3,
  py: 4,
  textAlign: 'center',
} as const;

/**
 * Public SEO/catalog routes stay anonymous. Leaving them without a session
 * provisions a guest. Returning signed-out users still go to login on private
 * routes so they can resume a real account.
 */
export const PriorLoginRouteGuard = ({ children }: { children: ReactNode }) => {
  const { isLoggedIn, authChecked, continueAsGuest } = useAuth();
  const location = useLocation();
  const anonymousReadable = isAnonymousReadablePath(location.pathname);
  const needsSession = authChecked && !isLoggedIn && !anonymousReadable;
  const returningSignedOut = needsSession && hasLoggedInBefore();
  const needsGuest = needsSession && !hasLoggedInBefore();
  const [guestError, setGuestError] = useState('');
  const provisioningPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (!needsGuest) {
      provisioningPathRef.current = null;
      setGuestError('');
      return;
    }

    const pathKey = `${location.pathname}${location.search}`;
    if (provisioningPathRef.current === pathKey) {
      return;
    }
    provisioningPathRef.current = pathKey;
    setGuestError('');

    void continueAsGuest().catch((error: unknown) => {
      provisioningPathRef.current = null;
      setGuestError(
        error instanceof Error ? error.message : 'Unable to start a guest session.',
      );
    });
  }, [continueAsGuest, location.pathname, location.search, needsGuest]);

  if (!authChecked) {
    if (hasLoggedInBefore() && !anonymousReadable) {
      const returnTo = `${location.pathname}${location.search}`;
      return (
        <Box sx={sessionCheckSx}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              Checking your session…
            </Typography>
            <Button
              variant="outlined"
              size="small"
              href={buildLoginPath(returnTo)}
              sx={touchNavButtonSx}
            >
              Log in
            </Button>
          </Stack>
        </Box>
      );
    }
    return <>{children}</>;
  }

  if (returningSignedOut) {
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={buildLoginPath(returnTo)} replace />;
  }

  if (needsGuest) {
    const returnTo = `${location.pathname}${location.search}`;
    return (
      <Box sx={sessionCheckSx}>
        <Stack spacing={2} alignItems="center">
          {guestError ? (
            <>
              <Typography variant="body2" color="error">
                {guestError}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                href={buildLoginPath(returnTo)}
                sx={touchNavButtonSx}
              >
                Log in
              </Button>
            </>
          ) : (
            <>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                Starting a guest session…
              </Typography>
            </>
          )}
        </Stack>
      </Box>
    );
  }

  return <>{children}</>;
};
